import { useTenantCode } from '@/lib/auth/TenantCodeContext';
import { useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/slices/authSlice';
import { useIsCustomerAdminBannerVisible } from '@/lib/auth/useIsCustomerAdminBannerVisible';

/**
 * Height in pixels of the impersonation banner. Exported so {@link AppLayout}
 * can reserve matching top-padding without hard-coding the same value twice.
 */
export const CUSTOMER_ADMIN_BANNER_HEIGHT_PX = 32;

/**
 * Persistent overlay shown to NVX administrators that have logged in as a
 * specific customer tenant. The banner is rendered with `position: fixed` so
 * it never participates in the surrounding layout flow — the rest of the UI
 * (sidebar, main content card, modals) is only shifted by the layout reserving
 * an equal amount of top padding when the banner is visible. This keeps the
 * inner content card's geometry untouched.
 */
export function CustomerAdminBanner() {
  const user = useAppSelector(selectUser);
  const routeTenantCode = useTenantCode();
  const isVisible = useIsCustomerAdminBannerVisible();

  if (!isVisible) {
    return null;
  }

  const tenantCode = user?.tenantCode ?? routeTenantCode ?? 'unknown';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{ height: `${CUSTOMER_ADMIN_BANNER_HEIGHT_PX}px` }}
      className="fixed inset-x-0 top-0 z-40 flex items-center justify-center bg-yellow-300 px-4 text-sm font-medium text-yellow-950 shadow-sm"
    >
      Administrator logged in for customer{' '}
      <span className="ml-1 font-semibold">{tenantCode}</span>
    </div>
  );
}
